import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { EventService, DynamicModuleService } from "../services/APIs";
import type { Event } from "../utils/types/event";

interface TierBenefit {
  text: string;
}

interface Tier {
  id: string;
  name: string;
  type: "paid" | "free";
  price: number;
  currency: string;
  benefits: TierBenefit[];
  registered: number;
  capacity: number;
}

const EventTickets = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("id");

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [tiersLoading, setTiersLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // First try to get event details from details endpoint
        const eventDetails = await EventService.getEventDetails(eventId, false);

        if (eventDetails) {
          setEvent(eventDetails);
        } else {
          // Fallback: try to get from all events
          const allEvents = await EventService.getAllEvents(false);
          const foundEvent = allEvents.find(e => e.id === eventId);

          if (foundEvent) {
            setEvent(foundEvent);
          } else {
            // Final fallback: try to get by ID if API supports it
            const eventData = await EventService.getEventById(eventId, false);
            setEvent(eventData);
          }
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  // Fetch ticket tiers from DynamicModuleService ("Add Ticket"), like BuyTicketPage
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setTiersLoading(true);

        const response = await DynamicModuleService.getGridCoByConfig(
          "Add Ticket",
          1,
          100,
          ["id", "category", "benefits", "price", "ticket", "soldticket"],
          eventId ? { jdid: eventId } : {}
        );

        let ticketsData: any[] = [];

        if (response?.results && Array.isArray(response.results)) {
          ticketsData = response.results;
        } else if (Array.isArray(response)) {
          ticketsData = response;
        } else if (
          response &&
          typeof response === "object" &&
          "id" in response
        ) {
          ticketsData = [response];
        }

        const mappedTiers: Tier[] = ticketsData.map(ticket => {
          const totalTickets = parseInt(ticket.ticket ?? "0", 10) || 0;
          const soldTickets = parseInt(ticket.soldticket ?? "0", 10) || 0;

          // Parse benefits (comma-separated string)
          const benefitsList: TierBenefit[] =
            typeof ticket.benefits === "string" &&
              ticket.benefits.trim().length > 0
              ? ticket.benefits
                .split(",")
                .map((b: string) => b.trim())
                .filter((b: string) => b.length > 0)
                .map((text: string) => ({ text }))
              : [];

          // Calculate price and determine type
          const price = ticket.price ? parseFloat(ticket.price) || 0 : 0;
          const type: "paid" | "free" = price === 0 ? "free" : "paid";

          return {
            id: ticket.id,
            name: ticket.category ?? "Ticket",
            type: type,
            price: price,
            currency: "USD",
            registered: soldTickets,
            capacity: totalTickets,
            benefits: benefitsList,
          };
        });

        setTiers(mappedTiers);
      } catch (error) {
        console.error("Error fetching tickets:", error);
        setTiers([]);
      } finally {
        setTiersLoading(false);
      }
    };

    if (eventId) {
      fetchTickets();
    } else {
      setTiers([]);
    }
  }, [eventId]);

  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading event...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-background p-4">
        <div className="mb-4 text-muted-foreground">Event not found</div>
        <Button
          onClick={() =>
            navigate(
              eventId ? `${CitycollabNavigationRoutes.EVENTS}/${eventId}` : CitycollabNavigationRoutes.EVENTS
            )
          }
        >
          Go Back
        </Button>
      </div>
    );
  }

  const registrationStart =
    (event.registration_start_date &&
      new Date(event.registration_start_date)) ??
    (event.start_date && new Date(event.start_date));

  const registrationEnd =
    (event.registration_end_date && new Date(event.registration_end_date)) ??
    (event.end_date && new Date(event.end_date));

  const eventName = event.event_name || "Event";

  const isRegistrationOpen = () => {
    const now = new Date();
    if (!registrationStart || !registrationEnd) {
      return false;
    }
    return now >= registrationStart && now <= registrationEnd;
  };

  const isTierAvailable = (tier: Tier) => {
    return tier.registered < tier.capacity && isRegistrationOpen();
  };

  const handlePayNow = (tier: Tier) => {
    if (!isTierAvailable(tier) || !eventId) {
      return;
    }
    navigate(
      `${CitycollabNavigationRoutes.EVENT_PAYMENT}?id=${encodeURIComponent(eventId)}&tier=${encodeURIComponent(tier.id)}`
    );
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                navigate(
                  eventId
                    ? `${CitycollabNavigationRoutes.EVENTS}/${eventId}`
                    : CitycollabNavigationRoutes.EVENTS
                )
              }
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Select Your Ticket</h1>
              <p className="text-sm text-muted-foreground">
                Choose the tier that works best for you
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-4 py-8">
        {/* Event Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">{eventName}</CardTitle>
            <CardDescription>Event Registration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Registration Opens</p>
                  <p className="text-sm text-muted-foreground">
                    {registrationStart ? formatDate(registrationStart) : "N/A"}
                  </p>
                </div>
              </div>
              <Separator orientation="vertical" className="h-12" />
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Registration Closes</p>
                  <p className="text-sm text-muted-foreground">
                    {registrationEnd ? formatDate(registrationEnd) : "N/A"}
                  </p>
                </div>
              </div>
            </div>
            {!isRegistrationOpen() && (
              <div className="mt-4 rounded-md border border-destructive/20 bg-destructive/10 p-3">
                <p className="text-sm font-medium text-destructive">
                  Registration is currently closed
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tier Listing */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tiersLoading && (
            <div className="col-span-full text-center text-sm text-muted-foreground">
              Loading tickets...
            </div>
          )}

          {!tiersLoading && tiers.length === 0 && (
            <div className="col-span-full text-center text-sm text-muted-foreground">
              No tickets available for this event.
            </div>
          )}

          {!tiersLoading &&
            tiers.length > 0 &&
            tiers.map(tier => {
              const available = isTierAvailable(tier);
              const soldOut = tier.registered >= tier.capacity;
              const slotsRemaining = tier.capacity - tier.registered;

              return (
                <Card
                  key={tier.id}
                  className={`relative transition-all ${available ? "hover:shadow-md" : "opacity-60"
                    }`}
                >
                  {soldOut && (
                    <Badge className="absolute right-3 top-3 bg-destructive">
                      Sold Out
                    </Badge>
                  )}
                  {tier.type === "free" && (
                    <Badge className="absolute right-3 top-3 bg-green-600">
                      Free
                    </Badge>
                  )}

                  <CardHeader>
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                    <CardDescription>
                      {tier.type === "paid" ? (
                        <span className="text-2xl font-bold text-foreground">
                          ${tier.price}
                          <span className="text-sm font-normal text-muted-foreground">
                            {" "}
                            {tier.currency}
                          </span>
                        </span>
                      ) : (
                        <span className="text-2xl font-bold text-green-600">
                          Free
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Slot Availability */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          Availability
                        </span>
                        <span
                          className={
                            soldOut
                              ? "font-medium text-destructive"
                              : "font-medium text-foreground"
                          }
                        >
                          {tier.registered}/{tier.capacity}
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-muted">
                        <div
                          className={`h-2 rounded-full transition-all ${soldOut ? "bg-destructive" : "bg-primary"
                            }`}
                          style={{
                            width: `${(tier.registered / tier.capacity) * 100}%`,
                          }}
                        />
                      </div>
                      {!soldOut && (
                        <p className="text-xs text-muted-foreground">
                          {slotsRemaining}{" "}
                          {slotsRemaining === 1 ? "slot" : "slots"} remaining
                        </p>
                      )}
                    </div>

                    <Separator />

                    {/* Benefits */}
                    <div>
                      <h4 className="mb-2 text-sm font-semibold">Benefits</h4>
                      <ul className="space-y-2">
                        {tier.benefits.map(benefit => (
                          <li
                            key={""}
                            className="flex items-start gap-2 text-sm"
                          >
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                            <span className="text-muted-foreground">
                              {benefit.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter>
                    <Button
                      className="w-full"
                      disabled={!available}
                      onClick={() => handlePayNow(tier)}
                    >
                      {soldOut
                        ? "Sold Out"
                        : available
                          ? "Pay Now"
                          : "Registration Closed"}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default EventTickets;
